import {useCallback, useState} from 'react'
import {View} from 'react-native'
import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {type BnuyFact as BnuyFactType, getRandomBnuyFact} from '#/lib/bnuyFacts'
import {atoms as a, useTheme} from '#/alf'
import {Sparkle_Stroke2_Corner0_Rounded as SparkleIcon} from '#/components/icons/Sparkle'
import {InlineLinkText} from '#/components/Link'
import {Text} from '#/components/Typography'

/**
 * Picks a random bunny fact, lazy-initialized so it only changes once per
 * mount unless `reroll` is called (e.g. on each tap of a "new fact" trigger).
 */
export function useBnuyFact(): [BnuyFactType, () => void] {
  const [fact, setFact] = useState(getRandomBnuyFact)
  const reroll = useCallback(() => setFact(getRandomBnuyFact()), [])
  return [fact, reroll]
}

/**
 * Presentational bunny-fact content, shared between the desktop sidebar card
 * and the mobile popover dialog.
 */
export function BnuyFactContent({fact}: {fact: BnuyFactType}) {
  const t = useTheme()
  const {_} = useLingui()

  return (
    <>
      <View style={[a.flex_row, a.align_center, a.gap_xs, a.pb_md]}>
        <SparkleIcon width={16} height={16} fill={t.atoms.text.color} />
        <Text
          emoji
          style={[a.flex_1, a.text_md, a.font_semi_bold, t.atoms.text]}>
          <Trans>Bnuy Facts 🐇</Trans>
        </Text>
      </View>

      <Text style={[a.text_sm, a.leading_snug, t.atoms.text_contrast_medium]}>
        {fact.fact}
      </Text>

      <Text
        style={[a.text_xs, a.leading_snug, a.pt_sm, t.atoms.text_contrast_low]}>
        <Trans>Source: </Trans>
        <InlineLinkText
          to={fact.sourceUrl}
          label={_(msg`View source for this rabbit fact`)}
          style={[t.atoms.text_contrast_medium]}>
          {fact.sourceLabel}
        </InlineLinkText>
      </Text>
    </>
  )
}
